import React, { useState } from 'react';
import { Workflow, Sparkles, CheckCircle2, AlertTriangle, Loader2, Database } from 'lucide-react';

export const DataLoadWorkbench = ({ project }) => {
  const [predicting, setPredicting] = useState(false);
  const [predictions, setPredictions] = useState(null);

  const predictLoadErrors = () => {
    setPredicting(true);
    setTimeout(() => {
      setPredictions({
        total_records: 2450000,
        predicted_errors: 12450,
        error_rate: 0.5,
        error_categories: [
          { category: 'Data Type Mismatch', count: 4523, severity: 'high', fix: 'Auto-convert with transformation rule' },
          { category: 'Missing Mandatory Fields', count: 3421, severity: 'high', fix: 'Data enrichment required' },
          { category: 'Referential Integrity', count: 2890, severity: 'medium', fix: 'Create missing master data' },
          { category: 'Value Range Violation', count: 1616, severity: 'low', fix: 'Adjust value ranges or cleanse data' }
        ],
        cleansing_rules: [
          { rule: 'Convert DATE format YYYYMMDD to ISO', objects_affected: 45, auto_applicable: true },
          { rule: 'Trim leading zeros from material numbers', objects_affected: 12, auto_applicable: true },
          { rule: 'Standardize country codes to ISO 3166', objects_affected: 23, auto_applicable: false }
        ],
        estimated_cleansing_days: 18
      });
      setPredicting(false);
    }, 3000);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Data Load Workbench (LTMC/LTMOM Assist)</h2>
            <p className="text-gray-600">Predict load errors and auto-generate transformation rules (PATENT)</p>
          </div>
          <button onClick={predictLoadErrors} disabled={predicting} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50">
            {predicting ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Predicting...</span></> : <><Sparkles className="w-5 h-5" /><span>AI Predict</span></>}
          </button>
        </div>
      </div>

      {!predictions && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Database className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <p className="text-gray-600 mb-6">AI-predict data load errors before execution</p>
          <button onClick={predictLoadErrors} className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700">Predict Load Errors</button>
        </div>
      )}

      {predictions && (
        <>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <div className="text-3xl font-bold mb-2">{(predictions.total_records / 1000000).toFixed(2)}M</div>
              <div className="text-blue-100 text-sm">Total Records</div>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
              <div className="text-3xl font-bold mb-2">{predictions.predicted_errors.toLocaleString()}</div>
              <div className="text-red-100 text-sm">Predicted Errors</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <div className="text-3xl font-bold mb-2">{predictions.error_rate}%</div>
              <div className="text-orange-100 text-sm">Error Rate</div>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <div className="text-3xl font-bold mb-2">{predictions.estimated_cleansing_days}</div>
              <div className="text-green-100 text-sm">Cleansing Days</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold mb-6">Predicted Error Categories</h3>
            <div className="space-y-3">
              {predictions.error_categories.map((cat, idx) => (
                <div key={idx} className="border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{cat.category}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          cat.severity === 'high' ? 'bg-red-100 text-red-700' :
                          cat.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>{cat.severity}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">Affected records: {cat.count.toLocaleString()}</p>
                      <p className="text-sm text-green-600 font-semibold">✓ Fix: {cat.fix}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold mb-6">AI-Generated Cleansing Rules</h3>
            <div className="space-y-3">
              {predictions.cleansing_rules.map((rule, idx) => (
                <div key={idx} className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">{rule.rule}</h4>
                      <p className="text-sm text-gray-600 mb-2">Affects {rule.objects_affected} objects</p>
                      {rule.auto_applicable ? (
                        <p className="text-sm text-green-600 font-semibold flex items-center space-x-1">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Auto-applicable</span>
                        </p>
                      ) : (
                        <p className="text-sm text-orange-600">Manual review required</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DataLoadWorkbench;